import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { FindingResponseInput } from '@/services/cases'
import { COLUMNS, type FindingState, isOpenState, isShown, type Responses } from '@/utils/findings'
import type { FindingView, PositionView } from '@backend/schemas'
import { AlertTriangle, CheckCircle2, HelpCircle, Loader2, MinusCircle, MinusSquare, PlusSquare, XCircle } from 'lucide-react'
import { Fragment, useState } from 'react'

export const StateIcon = ({ state }: { state?: FindingState }) => {
  switch (state) {
    case 'error':
      return <XCircle className="h-4 w-4 text-red-500" />
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />
    case 'question':
      return <HelpCircle className="h-4 w-4 text-blue-500" />
    case 'ok':
      return <CheckCircle2 className="h-4 w-4 text-green-600" />
    case 'na':
      return <MinusCircle className="h-4 w-4 text-muted-foreground" />
    default:
      return null
  }
}

type RespondProps = {
  responses: Responses
  pendingKey?: string
  onRespond: (input: FindingResponseInput) => void
}

export const FindingItem = ({ finding, responses, pendingKey, onRespond }: RespondProps & { finding: FindingView }) => {
  const response = responses[finding.key]
  const isPending = pendingKey === finding.key
  const answeredNo = finding.question && response?.answer === false
  const comment = answeredNo ? finding.question?.alertIfNo?.comment : (finding.question?.text ?? finding.alert?.comment)
  const canMarkNotApplicable = !finding.question && !!finding.alert

  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div className="flex items-start gap-2">
        <div className="pt-0.5">{isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <StateIcon state={finding.state} />}</div>
        <div>
          <p className={`font-medium ${finding.state === 'ok' || finding.state === 'na' ? 'text-muted-foreground' : ''}`}>
            {finding.label}
          </p>
          {comment && finding.state !== 'ok' && <p className="text-sm whitespace-pre-line">{comment}</p>}
        </div>
      </div>

      <div className="flex shrink-0 gap-1">
        {finding.question && (
          <>
            <Button
              size="sm"
              variant={response?.answer === true ? 'default' : 'outline'}
              disabled={isPending}
              onClick={() => onRespond({ key: finding.key, answer: true })}
            >
              Ja
            </Button>
            <Button
              size="sm"
              variant={response?.answer === false ? 'default' : 'outline'}
              disabled={isPending}
              onClick={() => onRespond({ key: finding.key, answer: false })}
            >
              Nej
            </Button>
          </>
        )}
        {canMarkNotApplicable && (
          <>
            <Button
              size="sm"
              variant={response?.action === 'N/A_for_car' ? 'default' : 'outline'}
              disabled={isPending}
              onClick={() => onRespond({ key: finding.key, action: 'N/A_for_car' })}
            >
              Ej relevant bil
            </Button>
            <Button
              size="sm"
              variant={response?.action === 'N/A_for_damage' ? 'default' : 'outline'}
              disabled={isPending}
              onClick={() => onRespond({ key: finding.key, action: 'N/A_for_damage' })}
            >
              Ej relevant skada
            </Button>
          </>
        )}
        {response && (
          <Button size="sm" variant="ghost" disabled={isPending} onClick={() => onRespond({ key: finding.key, clear: true })}>
            Ångra svar
          </Button>
        )}
      </div>
    </div>
  )
}


const PositionTitle = ({ position, findings }: { position: PositionView; findings: FindingView[] }) => {
  const hasOpenAlert = findings.some(finding => finding.active && (finding.state === 'error' || finding.state === 'warning'))

  return (
    <div className="flex items-center gap-2">
      {position.titleSource === 'unknown' ? (
        <span>
          <span className="text-muted-foreground">Okänd</span>
          {position.hint && <span> · {position.hint}</span>}
        </span>
      ) : (
        <span className="font-semibold">{position.title}</span>
      )}
      {isOpenState(position.cells.row) && <StateIcon state={position.cells.row} />}
      {!position.inEstimate && (
        <Badge variant={hasOpenAlert ? 'destructive' : 'outline'}>{hasOpenAlert ? 'Saknas i kalkylen' : 'Inte i kalkylen'}</Badge>
      )}
    </div>
  )
}

export const PositionFeedbackTable = ({
  positions,
  findings,
  ...respondProps
}: RespondProps & { positions: PositionView[]; findings: FindingView[] }) => {
  const [expanded, setExpanded] = useState<number | null>(null)

  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-muted/50">
          <TableHead className="w-8" />
          <TableHead className="w-12">Nr</TableHead>
          <TableHead>Position</TableHead>
          {COLUMNS.map(column => (
            <TableHead key={column.key} className="text-center">
              {column.label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {positions.map(position => {
          const isExpanded = expanded === position.position
          const positionFindings = findings.filter(
            finding => finding.position === position.position && isShown(finding, respondProps.responses),
          )
          const findingGroups = [
            { label: 'Hela positionen', findings: positionFindings.filter(finding => finding.column === null) },
            ...COLUMNS.map(column => ({
              label: column.label,
              findings: positionFindings.filter(finding => finding.column === column.key),
            })),
          ].filter(group => group.findings.length > 0)
          const isExpandable = findingGroups.length > 0

          return (
            <Fragment key={position.position}>
              <TableRow
                className={`${isExpandable ? 'cursor-pointer' : ''} ${isExpanded ? 'bg-orange-100 dark:bg-orange-950' : ''} ${
                  position.inEstimate ? '' : 'border-dashed bg-yellow-50/50 dark:bg-yellow-950/20'
                }`}
                onClick={isExpandable ? () => setExpanded(isExpanded ? null : position.position) : undefined}
              >
                <TableCell>
                  {isExpandable &&
                    (isExpanded ? <MinusSquare className="h-4 w-4" /> : <PlusSquare className="h-4 w-4" />)}
                </TableCell>
                <TableCell className="font-semibold">{position.position}</TableCell>
                <TableCell>
                  <PositionTitle position={position} findings={positionFindings} />
                </TableCell>
                {COLUMNS.map(column => (
                  <TableCell key={column.key}>
                    <div className="flex justify-center">
                      {isOpenState(position.cells[column.key]) && <StateIcon state={position.cells[column.key]} />}
                    </div>
                  </TableCell>
                ))}
              </TableRow>

              {isExpanded && isExpandable && (
                <TableRow>
                  <TableCell />
                  <TableCell colSpan={COLUMNS.length + 2} className="space-y-4">
                    {!position.inEstimate && <p className="text-sm text-muted-foreground">Positionen finns inte i kalkylen.</p>}
                    {findingGroups.map(group => (
                      <div key={group.label}>
                        <p className="text-sm font-semibold">{group.label}</p>
                        <div className="divide-y">
                          {group.findings.map(finding => (
                            <FindingItem key={finding.key} finding={finding} {...respondProps} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          )
        })}
      </TableBody>
    </Table>
  )
}
